import { CSSProperties, useState, useEffect } from 'react';
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import useFacebookConnections from "Hooks/useFacebookConnections";
import useScreen from "Hooks/useScreen";
import { PlaybookStepName, PlaybookType } from "modules/Const";
import { RecordDetails, RecordSelectorType, Track } from "modules/Types";
import { clsx } from "modules/Utils";
import RecordSearchView from "elements/RecordSearchView";
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import { Body2 } from 'components/shareable/Typography';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import ButtonSymphony from 'elements/ButtonSymphony';
import Banner from 'elements/Banner';
import Icon from 'elements/Icon';

// Extended RecordDetails type to include the properties we need
interface ExtendedRecordDetails extends RecordDetails {
    mainTrack?: Track;
    tracks?: Track[];
    highlightedTrack?: {
        id: string;
        uri: string;
        name: string;
    };
}

/** Custom option component for the track selector */
const CustomPlaylistSelectorOption = (props: any) => {
    const { children, data } = props;
    return (
        <div
            style={{
                display: 'flex',
                padding: 8
            }}
            {...props.innerProps}
        >
            <Body2>{children}</Body2>
            {data.explicit &&
                <div style={{
                    marginLeft: 8,
                    borderRadius: 2,
                    backgroundColor: Colors.greyLight,
                    padding: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>E</span>
                </div>
            }
        </div>
    );
};

interface RecordSelectorViewProps {
    className?: string;
    cssCalcHeight?: number;
    options?: RecordSelectorType[];
    handleSelectRecord?: (recordDetails: RecordDetails) => void;
}

const RecordSelectorView = ({
    className,
    cssCalcHeight,
    options = ['single', 'project', 'playlist'],
    handleSelectRecord: handleSelectRecordProp
}: RecordSelectorViewProps) => {
    const { cssCalc } = useScreen();
    const {
        playbookType,
        nextCampaignStep,
        setCurrentStep,
        setSelectedSongToPromote,
        setHighlightedTrack
    } = useCampaignBuilder();

    // State for the selected record and highlighted track
    const [recordSelected, setRecordSelected] = useState<ExtendedRecordDetails | null>(null);
    const [showTrackSelector, setShowTrackSelector] = useState<boolean>(false);
    const [trackSearchTerm, setTrackSearchTerm] = useState<string>('');
    const [trackOptions, setTrackOptions] = useState<SelectOptionItem[]>([]);

    // Reset states when component unmounts
    useEffect(() => {
        return () => {
            setRecordSelected(null);
            setShowTrackSelector(false);
            setTrackSearchTerm('');
            setTrackOptions([]);
        };
    }, []);

    // Update track options when record is selected
    useEffect(() => {
        if (recordSelected && recordSelected.tracks && recordSelected.tracks.length > 0) {
            const options = recordSelected.tracks.map((track: Track) => ({
                value: track.spotify_id || track.id || '',
                title: track.name,
                description: track.explicit ? 'Explicit' : undefined,
                explicit: track.explicit,
                artist: track.artist_names || '',
                object: track
            }));
            setTrackOptions(options);

            // Auto-select the first track
            if (options.length > 0) {
                const firstTrack = options[0];
                if (firstTrack.object) {
                    // Update the selected record with the track
                    setRecordSelected(prevState => ({
                        ...prevState!,
                        mainTrack: firstTrack.object
                    }));

                    // Update the search term to show the selected track name
                    setTrackSearchTerm(firstTrack.title || '');
                }
            }
        }
    }, [recordSelected?.id]); // Only run when the record ID changes, not on every record change

    const handleSelectRecord = async (recordDetails: RecordDetails) => {
        const extendedRecord = recordDetails as ExtendedRecordDetails;
        setRecordSelected(extendedRecord);

        // Check if this is a playlist for relevant playbook types
        const shouldShowTrackSelector =
            recordDetails.type === 'playlist' &&
            (playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS ||
                playbookType === PlaybookType.INCREASE_MUSIC_STREAMS);

        if (shouldShowTrackSelector) {
            setShowTrackSelector(true);
        } else {
            // Regular flow - just set the song and continue
            setSelectedSongToPromote(recordDetails);

            handleSelectRecordProp?.(recordDetails);

            nextCampaignStep()
        }
    }

    const handleTrackSelect = (option: SelectOptionItem) => {
        if (recordSelected && option.object) {
            setRecordSelected({
                ...recordSelected,
                mainTrack: option.object
            });

            setTrackSearchTerm(option.title || '');
        }
    };

    const handleContinueWithTrack = () => {
        if (recordSelected && recordSelected.mainTrack) {
            const highlightedTrack = {
                id: recordSelected.mainTrack.id || recordSelected.mainTrack.spotify_id || '',
                uri: recordSelected.mainTrack.uri || recordSelected.mainTrack.spotify_uri || '',
                name: recordSelected.mainTrack.name || ''
            };

            setHighlightedTrack(highlightedTrack);

            const recordWithTrack = {
                ...recordSelected,
                highlightedTrack
            };

            setSelectedSongToPromote(recordWithTrack);

            if (playbookType === PlaybookType.INCREASE_MUSIC_STREAMS ||
                playbookType === PlaybookType.GROW_PLAYLIST_FOLLOWERS) {
                setCurrentStep(PlaybookStepName.CREATIVE);
            }
        }
    };

    const renderRecordSelectorTabInner = () => {
        if (showTrackSelector && recordSelected) {
            return (
                <div className="trackSelectorContainer" style={sty.trackSelectorContainer}>
                    <div style={sty.recordInfoContainer}>
                        <div style={sty.recordImage}>
                            <img
                                src={recordSelected.thumbnail_url}
                                alt={recordSelected.name}
                                style={sty.recordThumbnail}
                            />
                        </div>
                        <div style={sty.recordInfo}>
                            <h3 style={Sty.text18bold}>{recordSelected.name}</h3>
                            <p style={Sty.text14grey}>{recordSelected.primary_artist}</p>
                        </div>
                    </div>

                    <Banner
                        color="maestro"
                        className="infoSection" style={sty.infoSection}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Icon
                                className="maestroIcon"
                                icon="maestroBadgeImage" />
                            <h3 style={Sty.text18bold}>Autoplay a Track</h3>
                        </div>
                        <p style={Sty.text14grey}>You can select a track from your playlist to autoplay when someone clicks on your ad.</p>

                        <div style={{ paddingTop: 16, paddingBottom: 16 }}>
                            <InputSymphony
                                className="trackSelector"
                                label="Search for a track to autoplay"
                                value={trackSearchTerm}
                                onChange={setTrackSearchTerm}
                                placeholder="Select a song"
                                isSelectDropdown
                                options={trackOptions}
                                onOptionSelect={handleTrackSelect}
                            />
                        </div>
                    </Banner>
                    <div style={sty.buttonContainer}>
                        <ButtonSymphony
                            className="backButton"
                            onClick={() => setShowTrackSelector(false)}
                            variant="outlined"
                            color="grey"
                        >
                            Back to Playlist Selection
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="continueButton"
                            onClick={handleContinueWithTrack}
                            variant="outlined"
                            color="purple"
                        >
                            {!recordSelected?.mainTrack ? 'Skip' : 'Continue'}
                        </ButtonSymphony>
                    </div>
                </div>
            );
        }

        return (
            <div className="recordSelectorTabInner middle-wrapper lg:px-0 relative">
                <RecordSearchView
                    className="recordSelectorTabRecordSearchView"
                    handleSelectRecord={handleSelectRecord}
                    options={options}
                    letUserSearchSpotify
                    letUserRefreshReleases
                    // maxHeight={cssCalc(72 + 72 + 64 + 276)}
                    maxHeight={cssCalc(cssCalcHeight)}
                />
            </div>
        );
    }

    return (
        <div
            className={clsx('recordSelectorViewComponent', className)}
            style={sty.recordSelectorViewComponent}
        >
            {renderRecordSelectorTabInner()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    recordSelectorViewComponent: {
        width: '100%',
        backgroundColor: 'white',
    },
    trackSelectorContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        padding: 16,
    },
    recordInfoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    recordImage: {
        width: 80,
        height: 80,
        borderRadius: 4,
        overflow: 'hidden',
    },
    recordThumbnail: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    recordInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    infoSection: {
        backgroundColor: Colors.greyLighter,
        padding: 16,
        borderRadius: 8,
        marginTop: 16,
        overflow: 'visible',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 24,
    },
}

export default RecordSelectorView;